import { createBrowserRouter, RouterProvider, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/hooks/useAuth';
import { Toaster } from '@/components/ui/toaster';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ConfigNotice } from '@/components/ConfigNotice';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Analytics } from '@/components/Analytics';
import { StructuredData } from '@/components/StructuredData';
import SharedInvoice from '@/pages/SharedInvoice';
import SharedQuotation from '@/pages/SharedQuotation';

// Pages
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import InvoiceBuilder from '@/pages/InvoiceBuilder';
import InvoiceList from '@/pages/InvoiceList';
import InvoicePreview from '@/pages/InvoicePreview';
import QuotationList from '@/pages/QuotationList';
import QuotationBuilder from '@/pages/QuotationBuilder';
import QuotationPreview from '@/pages/QuotationPreview';
import Profile from '@/pages/Profile';
import Subscription from '@/pages/Subscription';
import Pricing from '@/pages/Pricing';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsConditions from '@/pages/TermsConditions';
import RefundPolicy from '@/pages/RefundPolicy';
import Security from '@/pages/Security';
import InitStats from '@/pages/InitStats';
import EmailVerification from '@/pages/EmailVerification';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import Admin from '@/pages/Admin';
import Verify2Fa from '@/pages/Verify2Fa';
import ProAnalytics from '@/pages/ProAnalytics';
import TryItOut from '@/pages/TryItOut';
import NotFound from '@/pages/NotFound';
import About from '@/pages/About';
import Help from '@/pages/Help';
import FAQ from '@/pages/FAQ';
import Guide from '@/pages/Guide';
import Templates from '@/pages/Templates';
import ProfessionInvoiceTemplate from '@/pages/ProfessionInvoiceTemplate';

function AppContent() {
  const location = useLocation();
  const isSharedView = location.pathname.startsWith('/share/');

  return (
    <div className="flex flex-col min-h-screen">
      {!isSharedView && (
        <header className="print:hidden">
          <ConfigNotice />
          <Navbar />
        </header>
      )}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/init-stats" element={<InitStats />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/try-it-out" element={<TryItOut />} />
          <Route path="/about" element={<About />} />
          <Route path="/help" element={<Help />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/invoice-template/:profession" element={<ProfessionInvoiceTemplate />} />
          
          {/* Public Sharing Routes */}
          <Route path="/share/invoice/:userId/:invoiceId" element={<SharedInvoice />} />
          <Route path="/share/quotation/:userId/:quotationId" element={<SharedQuotation />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute>
                <InvoiceBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/edit/:id"
            element={
              <ProtectedRoute>
                <InvoiceBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/preview/:id"
            element={
              <ProtectedRoute>
                <InvoicePreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations"
            element={
              <ProtectedRoute>
                <QuotationList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/new"
            element={
              <ProtectedRoute>
                <QuotationBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/edit/:id"
            element={
              <ProtectedRoute>
                <QuotationBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/preview/:id"
            element={
              <ProtectedRoute>
                <QuotationPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff"
            element={<Admin />}
          />
          <Route
            path="/verify-2fa"
            element={<Verify2Fa />}
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <ProAnalytics />
              </ProtectedRoute>
            }
          />
          {/* Dedicated 404 Route & Catch-all Redirect */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </main>
      {!isSharedView && (
        <div className="print:hidden">
          <Footer />
        </div>
      )}
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <>
        <Analytics />
        <StructuredData />
        <AuthProvider>
          <AppContent />
          <Toaster />
        </AuthProvider>
      </>
    )
  }
]);

export default function App() {
  return <RouterProvider router={router} />;
}
